/**
 * API functions for retrieving user interaction reports from the DenoKV backend
 * This file has been refactored to work with the new DenoKV-based backend
 */

// Get the DenoKV API URL from environment variables or use a default
const DENOKV_API_URL = process.env.REACT_APP_DENOKV_API_URL || 'http://localhost:8000';

// Helper function to convert date to timestamp
function dateToTimestamp(dateStr) {
  if (!dateStr) return undefined;
  const date = new Date(dateStr);
  return date.getTime();
}

// Helper function to format timestamp for display
function formatTimestamp(timestamp) {
  if (!timestamp) return '';
  return new Date(timestamp).toISOString().split('T')[0];
}

// Helper function to group interactions by day
function groupByDay(interactions) {
  const groupedByDay = {};
  
  interactions.forEach(interaction => {
    const date = new Date(interaction.timestamp);
    const day = date.toISOString().split('T')[0];
    
    if (!groupedByDay[day]) {
      groupedByDay[day] = 0;
    }
    
    groupedByDay[day]++;
  });
  
  return groupedByDay;
}

// Helper function to convert grouped data to array format for charts
function groupedToArray(grouped) {
  return Object.entries(grouped)
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Fetch user interaction data with filtering
 * @param {Object} filters - Filters to apply to the data
 * @param {string} filters.startDate - Start date in ISO format (YYYY-MM-DD)
 * @param {string} filters.endDate - End date in ISO format (YYYY-MM-DD)
 * @param {Array<string>} filters.interactionTypes - Array of interaction types to include
 * @param {Array<string>} filters.userIds - Array of user IDs to include
 * @param {Array<string>} filters.components - Array of component names to include
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @returns {Promise<Object>} - Promise resolving to the interaction data
 */
export async function fetchUserInteractionData(filters = {}, page = 1, pageSize = 100) {
  try {
    const {
      startDate,
      endDate,
      interactionTypes = [],
      userIds = [],
      components = []
    } = filters;

    // Build the URL with query parameters
    const url = new URL(`${DENOKV_API_URL}/api/userInteractions`);
    
    // Convert dates to timestamps for the DenoKV backend
    const startTime = startDate ? dateToTimestamp(startDate) : undefined;
    const endTime = endDate ? dateToTimestamp(endDate) + (24 * 60 * 60 * 1000 - 1) : undefined; // End of day
    
    // Add filters as query parameters
    if (startTime) {
      url.searchParams.append('startTime', startTime.toString());
    }
    
    if (endTime) {
      url.searchParams.append('endTime', endTime.toString());
    }
    
    // Add type filter if only one type is selected
    if (interactionTypes.length === 1) {
      url.searchParams.append('type', interactionTypes[0]);
    }
    
    // Add user filter if only one user is selected
    if (userIds.length === 1) {
      url.searchParams.append('userId', userIds[0]);
    }
    
    // Add component filter if only one component is selected
    if (components.length === 1) {
      url.searchParams.append('componentName', components[0]);
    }
    
    // Add pagination parameters
    url.searchParams.append('limit', pageSize.toString());
    
    // Add cursor for pagination if not the first page
    if (page > 1) {
      // In a real implementation, we would store and pass the cursor from previous requests
      // For now, we'll just skip items based on page number
      const skip = (page - 1) * pageSize;
      url.searchParams.append('skip', skip.toString());
    }
    
    // Execute the query
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      credentials: 'omit'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch interaction data: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // Filter the results client-side if multiple types, users, or components are selected
    let filteredInteractions = data.interactions || [];
    
    if (interactionTypes.length > 1) {
      filteredInteractions = filteredInteractions.filter(interaction => 
        interactionTypes.includes(interaction.type)
      );
    }
    
    if (userIds.length > 1) {
      filteredInteractions = filteredInteractions.filter(interaction => 
        userIds.includes(interaction.userId)
      );
    }
    
    if (components.length > 1) {
      filteredInteractions = filteredInteractions.filter(interaction => 
        components.includes(interaction.componentName)
      );
    }
    
    return {
      data: filteredInteractions,
      count: filteredInteractions.length,
      total: data.total || filteredInteractions.length,
      page,
      pageSize,
      cursor: data.cursor
    };
  } catch (error) {
    console.error('Error fetching user interaction data:', error);
    
    // Return empty data on error
    return {
      data: [],
      count: 0,
      total: 0,
      page,
      pageSize,
      error: error.message
    };
  }
}

/**
 * Fetch user interaction summary statistics
 * @param {Object} filters - Filters to apply to the data
 * @returns {Promise<Object>} - Promise resolving to the summary statistics
 */
export async function fetchInteractionSummary(filters = {}) {
  try {
    const {
      startDate,
      endDate,
      interactionTypes = [],
      userIds = [],
      components = []
    } = filters;

    // Build the URL with query parameters
    const url = new URL(`${DENOKV_API_URL}/api/userInteractions/summary`);
    
    // Convert dates to timestamps for the DenoKV backend
    const startTime = startDate ? dateToTimestamp(startDate) : undefined;
    const endTime = endDate ? dateToTimestamp(endDate) + (24 * 60 * 60 * 1000 - 1) : undefined; // End of day
    
    // Add filters as query parameters
    if (startTime) {
      url.searchParams.append('startTime', startTime.toString());
    }
    
    if (endTime) {
      url.searchParams.append('endTime', endTime.toString());
    }
    
    // Add user filter if only one user is selected
    if (userIds.length === 1) {
      url.searchParams.append('userId', userIds[0]);
    }
    
    // Add session filter if provided
    const sessionId = filters.sessionId;
    if (sessionId) {
      url.searchParams.append('sessionId', sessionId);
    }
    
    // Execute the query
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      credentials: 'omit'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch interaction summary: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // Process the data to match the expected format
    const summary = {
      totalInteractions: data.totalInteractions || 0,
      interactionsByType: data.interactionsByType || {},
      interactionsByComponent: data.interactionsByComponent || {},
      interactionsByUser: data.interactionsByUser || {},
      timeRange: data.timeRange || { startTime, endTime }
    };
    
    // Convert interactionsByType to mostCommonInteractions array
    const mostCommonInteractions = Object.entries(summary.interactionsByType)
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count);
    
    // Convert interactionsByComponent to mostActiveComponents array
    const mostActiveComponents = Object.entries(summary.interactionsByComponent)
      .map(([component, count]) => ({ component, count }))
      .sort((a, b) => b.count - a.count);
    
    // Convert interactionsByUser to mostActiveUsers array
    const mostActiveUsers = Object.entries(summary.interactionsByUser)
      .map(([userId, count]) => ({ userId, count }))
      .sort((a, b) => b.count - a.count);
    
    // Add the arrays to the summary
    summary.mostCommonInteractions = mostCommonInteractions;
    summary.mostActiveComponents = mostActiveComponents;
    summary.mostActiveUsers = mostActiveUsers;
    
    // If we have raw interactions data, group by day
    if (data.interactions) {
      summary.interactionsByDay = groupByDay(data.interactions);
      summary.interactionsByDayArray = groupedToArray(summary.interactionsByDay);
    } else {
      summary.interactionsByDay = {};
      summary.interactionsByDayArray = [];
    }
    
    return summary;
  } catch (error) {
    console.error('Error fetching interaction summary:', error);
    
    // Return empty summary on error
    return {
      totalInteractions: 0,
      interactionsByType: {},
      interactionsByComponent: {},
      interactionsByUser: {},
      interactionsByDay: {},
      mostActiveUsers: [],
      mostActiveComponents: [],
      mostCommonInteractions: [],
      interactionsByDayArray: [],
      error: error.message
    };
  }
}

/**
 * Fetch page view statistics
 * @param {Object} filters - Filters to apply to the data
 * @returns {Promise<Object>} - Promise resolving to the page view statistics
 */
export async function fetchPageViewStatistics(filters = {}) {
  try {
    const {
      startDate,
      endDate,
      userIds = []
    } = filters;

    // Build the URL with query parameters
    const url = new URL(`${DENOKV_API_URL}/api/userInteractions/pageViews`);
    
    // Convert dates to timestamps for the DenoKV backend
    const startTime = startDate ? dateToTimestamp(startDate) : undefined;
    const endTime = endDate ? dateToTimestamp(endDate) + (24 * 60 * 60 * 1000 - 1) : undefined; // End of day
    
    // Add filters as query parameters
    if (startTime) {
      url.searchParams.append('startTime', startTime.toString());
    }
    
    if (endTime) {
      url.searchParams.append('endTime', endTime.toString());
    }
    
    // Add limit parameter
    url.searchParams.append('limit', '100');
    
    // Execute the query
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      credentials: 'omit'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch page view statistics: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // Process the data to match the expected format
    const pageViews = data.pageViews || [];
    
    // Create a map of page views by page
    const pageViewsByPage = {};
    pageViews.forEach(pageView => {
      pageViewsByPage[pageView.pageName] = pageView.count;
    });
    
    // Calculate total page views
    const totalPageViews = pageViews.reduce((total, pageView) => total + pageView.count, 0);
    
    // Create a map of page views by user (if available)
    const pageViewsByUser = {};
    
    // Create a map of page views by day (if raw data is available)
    let pageViewsByDay = {};
    let pageViewsByDayArray = [];
    
    // If we have raw interactions data, group by day
    if (data.interactions) {
      pageViewsByDay = groupByDay(data.interactions);
      pageViewsByDayArray = groupedToArray(pageViewsByDay);
    }
    
    return {
      totalPageViews,
      pageViewsByPage,
      pageViewsByUser,
      pageViewsByDay,
      mostViewedPages: pageViews,
      pageViewsByDayArray
    };
  } catch (error) {
    console.error('Error fetching page view statistics:', error);
    
    // Return empty statistics on error
    return {
      totalPageViews: 0,
      pageViewsByPage: {},
      pageViewsByUser: {},
      pageViewsByDay: {},
      mostViewedPages: [],
      pageViewsByDayArray: [],
      error: error.message
    };
  }
}

/**
 * Fetch button click statistics
 * @param {Object} filters - Filters to apply to the data
 * @returns {Promise<Object>} - Promise resolving to the button click statistics
 */
export async function fetchButtonClickStatistics(filters = {}) {
  try {
    const {
      startDate,
      endDate,
      userIds = [],
      components = []
    } = filters;

    // Build the URL with query parameters
    const url = new URL(`${DENOKV_API_URL}/api/userInteractions/buttonClicks`);
    
    // Convert dates to timestamps for the DenoKV backend
    const startTime = startDate ? dateToTimestamp(startDate) : undefined;
    const endTime = endDate ? dateToTimestamp(endDate) + (24 * 60 * 60 * 1000 - 1) : undefined; // End of day
    
    // Add filters as query parameters
    if (startTime) {
      url.searchParams.append('startTime', startTime.toString());
    }
    
    if (endTime) {
      url.searchParams.append('endTime', endTime.toString());
    }
    
    // Add component filter if only one component is selected
    if (components.length === 1) {
      url.searchParams.append('componentName', components[0]);
    }
    
    // Add limit parameter
    url.searchParams.append('limit', '100');
    
    // Execute the query
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      credentials: 'omit'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch button click statistics: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // Process the data to match the expected format
    const buttonClicks = data.buttonClicks || [];
    
    // Create a map of button clicks by button
    const buttonClicksByButton = {};
    buttonClicks.forEach(buttonClick => {
      buttonClicksByButton[buttonClick.buttonName] = buttonClick.count;
    });
    
    // Create a map of button clicks by component
    const buttonClicksByComponent = {};
    buttonClicks.forEach(buttonClick => {
      if (!buttonClicksByComponent[buttonClick.componentName]) {
        buttonClicksByComponent[buttonClick.componentName] = 0;
      }
      buttonClicksByComponent[buttonClick.componentName] += buttonClick.count;
    });
    
    // Calculate total button clicks
    const totalButtonClicks = buttonClicks.reduce((total, buttonClick) => total + buttonClick.count, 0);
    
    // Create a map of button clicks by user (if available)
    const buttonClicksByUser = {};
    
    // Create a map of button clicks by day (if raw data is available)
    let buttonClicksByDay = {};
    let buttonClicksByDayArray = [];
    
    // If we have raw interactions data, group by day
    if (data.interactions) {
      buttonClicksByDay = groupByDay(data.interactions);
      buttonClicksByDayArray = groupedToArray(buttonClicksByDay);
    }
    
    return {
      totalButtonClicks,
      buttonClicksByButton,
      buttonClicksByComponent,
      buttonClicksByUser,
      buttonClicksByDay,
      mostClickedButtons: buttonClicks,
      buttonClicksByDayArray
    };
  } catch (error) {
    console.error('Error fetching button click statistics:', error);
    
    // Return empty statistics on error
    return {
      totalButtonClicks: 0,
      buttonClicksByButton: {},
      buttonClicksByComponent: {},
      buttonClicksByUser: {},
      buttonClicksByDay: {},
      mostClickedButtons: [],
      buttonClicksByDayArray: [],
      error: error.message
    };
  }
}

/**
 * Fetch form submission statistics
 * @param {Object} filters - Filters to apply to the data
 * @returns {Promise<Object>} - Promise resolving to the form submission statistics
 */
export async function fetchFormSubmissionStatistics(filters = {}) {
  try {
    const {
      startDate,
      endDate,
      userIds = [],
      components = []
    } = filters;

    // Build the URL with query parameters
    const url = new URL(`${DENOKV_API_URL}/api/userInteractions/formSubmissions`);
    
    // Add filters as query parameters
    if (startDate) {
      url.searchParams.append('startDate', startDate);
    }
    
    if (endDate) {
      url.searchParams.append('endDate', endDate);
    }
    
    if (userIds.length > 0) {
      url.searchParams.append('userIds', userIds.join(','));
    }
    
    if (components.length > 0) {
      url.searchParams.append('components', components.join(','));
    }
    
    // Execute the query
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      credentials: 'omit'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch form submission statistics: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching form submission statistics:', error);
    
    // Return empty statistics on error
    return {
      totalFormSubmissions: 0,
      formSubmissionsByForm: {},
      formSubmissionsByUser: {},
      formSubmissionsByDay: {},
      mostSubmittedForms: [],
      formSubmissionsByDayArray: [],
      formSubmissionSuccess: {
        successful: 0,
        failed: 0
      },
      error: error.message
    };
  }
}

/**
 * Fetch map interaction statistics
 * @param {Object} filters - Filters to apply to the data
 * @returns {Promise<Object>} - Promise resolving to the map interaction statistics
 */
export async function fetchMapInteractionStatistics(filters = {}) {
  try {
    const {
      startDate,
      endDate,
      userIds = []
    } = filters;

    // Build the URL with query parameters
    const url = new URL(`${DENOKV_API_URL}/api/userInteractions/mapInteractions`);
    
    // Add filters as query parameters
    if (startDate) {
      url.searchParams.append('startDate', startDate);
    }
    
    if (endDate) {
      url.searchParams.append('endDate', endDate);
    }
    
    if (userIds.length > 0) {
      url.searchParams.append('userIds', userIds.join(','));
    }
    
    // Execute the query
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      credentials: 'omit'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch map interaction statistics: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching map interaction statistics:', error);
    
    // Return empty statistics on error
    return {
      totalMapInteractions: 0,
      mapInteractionsByType: {},
      mapInteractionsByLayer: {},
      mapInteractionsByUser: {},
      mapInteractionsByDay: {},
      mostActiveMapLayers: [],
      mapInteractionsByDayArray: [],
      error: error.message
    };
  }
}

/**
 * Get all unique interaction types
 * @returns {Promise<Array<string>>} - Promise resolving to an array of interaction types
 */
export async function getInteractionTypes() {
  try {
    const response = await fetch(`${DENOKV_API_URL}/api/userInteractions/types`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      credentials: 'omit'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch interaction types: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching interaction types:', error);
    
    // Return default types on error
    return [
      'page_view',
      'component_view',
      'button_click',
      'form_submit',
      'map_interaction',
      'map_layer_toggle',
      'todo_action',
      'time_track_action'
    ];
  }
}

/**
 * Get all unique component names
 * @returns {Promise<Array<string>>} - Promise resolving to an array of component names
 */
export async function getComponentNames() {
  try {
    const response = await fetch(`${DENOKV_API_URL}/api/userInteractions/components`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      credentials: 'omit'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch component names: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching component names:', error);
    
    // Return default components on error
    return [
      'Map',
      'TimeTrack',
      'TodoList',
      'TimeCard',
      'HomePage',
      'Dashboard'
    ];
  }
}

/**
 * Get all users who have interactions
 * @returns {Promise<Array<Object>>} - Promise resolving to an array of user objects
 */
export async function getUsersWithInteractions() {
  try {
    const response = await fetch(`${DENOKV_API_URL}/api/userInteractions/users`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      credentials: 'omit'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch users with interactions: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching users with interactions:', error);
    
    // Return empty array on error
    return [];
  }
}

/**
 * Export interaction data to CSV
 * @param {Object} filters - Filters to apply to the data
 * @returns {Promise<string>} - Promise resolving to a CSV string
 */
export async function exportInteractionsToCSV(filters = {}) {
  try {
    // Fetch the data with a large limit
    const result = await fetchUserInteractionData(filters, 1, 10000);
    const interactions = result.data;
    
    if (!interactions || interactions.length === 0) {
      return 'No data to export';
    }
    
    // Create CSV header
    const headers = [
      'ID',
      'Type',
      'User ID',
      'Session ID',
      'Timestamp',
      'Component',
      'Additional Data'
    ];
    
    // Create CSV rows
    const rows = interactions.map(interaction => [
      interaction.id,
      interaction.type,
      interaction.userId,
      interaction.sessionId,
      new Date(interaction.timestamp).toISOString(),
      interaction.data?.componentName || '',
      JSON.stringify(interaction.data || {})
    ]);
    
    // Combine header and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    return csvContent;
  } catch (error) {
    console.error('Error exporting interactions to CSV:', error);
    return `Error: ${error.message}`;
  }
}
